import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const GENDER_OPTIONS = [
  { value: "ferfi", label: "Férfi" },
  { value: "no", label: "Nő" },
  { value: "egyeb", label: "Egyéb" },
];

export const STATUS_OPTIONS = [
  { value: "active", label: "Aktív", tone: "emerald" },
  { value: "probation", label: "Próbaidő", tone: "amber" },
  { value: "notice", label: "Felmondás", tone: "orange" },
  { value: "inactive", label: "Inaktív", tone: "muted" },
];

export const STATUS_TONES = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  amber:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
  orange:
    "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-400",
  muted: "border-border bg-muted text-muted-foreground",
};

export const STATUS_DOT_TONES = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  orange: "bg-orange-500",
  muted: "bg-muted-foreground/60",
};

const NULL_SELECT = "__null__";

const emptyForm = {
  titulus: "",
  last_name: "",
  first_name: "",
  birth_last_name: "",
  birth_first_name: "",
  gender: "",
  citizenship: "magyar",
  status: "active",
  mother_name: "",
  birth_place: "",
  birth_date: "",
  taj: "",
  tax_id: "",
  id_card_number: "",
  pos_id: "",
  pos_password: "",
  address: "",
  mailing_address: "",
  email: "",
  phone: "",
  company_id: "",
  site_id: "",
  position: "",
  bank_account_number: "",
  bank_name: "",
  payment_method: "",
};

export function EmployeeFormSheet({
  open,
  onOpenChange,
  employee,
  defaultCompanyId,
  defaultSiteId,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);

  // Load companies + sites whenever the sheet opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: cs }, { data: ss }] = await Promise.all([
          api.get("/companies"),
          api.get("/sites"),
        ]);
        setCompanies(cs);
        setSites(ss);
      } catch (e) {
        // non-fatal: company/site selects will be empty
      }
    })();
  }, [open]);

  // Reset / hydrate form whenever sheet opens or employee changes
  useEffect(() => {
    if (!open) return;
    if (employee) {
      setForm({
        titulus: employee.titulus || "",
        last_name: employee.last_name || "",
        first_name: employee.first_name || "",
        birth_last_name: employee.birth_last_name || "",
        birth_first_name: employee.birth_first_name || "",
        gender: employee.gender || "",
        citizenship: employee.citizenship || "",
        status: employee.status || "active",
        mother_name: employee.mother_name || "",
        birth_place: employee.birth_place || "",
        birth_date: employee.birth_date || "",
        taj: employee.taj || "",
        tax_id: employee.tax_id || "",
        id_card_number: employee.id_card_number || "",
        pos_id: employee.pos_id || "",
        pos_password: employee.pos_password || "",
        address: employee.address || "",
        mailing_address: employee.mailing_address || "",
        email: employee.email || "",
        phone: employee.phone || "",
        company_id: employee.company_id || "",
        site_id: employee.site_id || "",
        position: employee.position || "",
        bank_account_number: employee.bank_account_number || "",
        bank_name: employee.bank_name || "",
        payment_method: employee.payment_method || "",
      });
    } else {
      setForm({
        ...emptyForm,
        company_id: defaultCompanyId || "",
        site_id: defaultSiteId || "",
      });
    }
    setFormError("");
  }, [open, employee, defaultCompanyId, defaultSiteId]);

  // Filter sites by selected company
  const visibleSites = form.company_id
    ? sites.filter((s) => s.company_id === form.company_id)
    : sites;

  const onCompanyChange = (value) => {
    const next = value === NULL_SELECT ? "" : value;
    setForm((f) => {
      const siteStillValid =
        next === "" || sites.find((s) => s._id === f.site_id)?.company_id === next;
      return {
        ...f,
        company_id: next,
        site_id: siteStillValid ? f.site_id : "",
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.last_name = form.last_name;
      payload.first_name = form.first_name;
      payload.status = form.status || "active";

      let result;
      if (employee) {
        const { data } = await api.patch(`/employees/${employee._id}`, payload);
        result = data;
        toast.success("Munkavállaló frissítve.");
      } else {
        const { data } = await api.post("/employees", payload);
        result = data;
        toast.success("Munkavállaló létrehozva.");
      }
      onOpenChange(false);
      if (onSaved) onSaved(result);
    } catch (err) {
      setFormError(
        formatApiError(err?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="employee-form-sheet"
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b border-border px-6 py-5">
          <SheetTitle className="tracking-tight">
            {employee ? "Munkavállaló szerkesztése" : "Új munkavállaló"}
          </SheetTitle>
          <SheetDescription>
            {employee
              ? "Frissítsd a munkavállaló adatait. A változások azonnal mentésre kerülnek."
              : "Add meg a munkavállaló adatait. A vezetéknév és keresztnév kötelező."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <Section title="Személyes adatok">
              <Grid cols={2}>
                <Field label="Titulus" testId="form-titulus">
                  <Input
                    value={form.titulus}
                    onChange={(e) => setForm((f) => ({ ...f, titulus: e.target.value }))}
                    placeholder="dr., Mrs., …"
                  />
                </Field>
                <Field label="Nem" testId="form-gender">
                  <Select
                    value={form.gender || NULL_SELECT}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, gender: v === NULL_SELECT ? "" : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NULL_SELECT}>—</SelectItem>
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g.value} value={g.value}>
                          {g.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </Grid>
              <Grid cols={2}>
                <Field label="Vezetéknév *" testId="form-last-name">
                  <Input
                    required
                    value={form.last_name}
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                    placeholder="Kovács"
                  />
                </Field>
                <Field label="Keresztnév *" testId="form-first-name">
                  <Input
                    required
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    placeholder="Péter"
                  />
                </Field>
              </Grid>
              <Grid cols={2}>
                <Field label="Születési vezetéknév" testId="form-birth-last-name">
                  <Input
                    value={form.birth_last_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, birth_last_name: e.target.value }))
                    }
                    placeholder="Kovács"
                  />
                </Field>
                <Field label="Születési keresztnév" testId="form-birth-first-name">
                  <Input
                    value={form.birth_first_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, birth_first_name: e.target.value }))
                    }
                    placeholder="Péter"
                  />
                </Field>
              </Grid>
              <Grid cols={2}>
                <Field label="Állampolgárság" testId="form-citizenship">
                  <Input
                    value={form.citizenship}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, citizenship: e.target.value }))
                    }
                    placeholder="magyar"
                  />
                </Field>
                <Field label="Státusz" testId="form-status">
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </Grid>
            </Section>

            <Section title="Munkaviszony">
              <Grid cols={2}>
                <Field label="Cég" testId="form-company">
                  <Select
                    value={form.company_id || NULL_SELECT}
                    onValueChange={onCompanyChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NULL_SELECT}>— Nincs megadva —</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c._id} value={c._id}>
                          {c.name}
                          {c.type === "franchise" ? " · franchise" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Telephely" testId="form-site">
                  <Select
                    value={form.site_id || NULL_SELECT}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        site_id: v === NULL_SELECT ? "" : v,
                      }))
                    }
                    disabled={visibleSites.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          form.company_id
                            ? visibleSites.length === 0
                              ? "Nincs telephely"
                              : "—"
                            : "Előbb válassz céget"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NULL_SELECT}>— Nincs megadva —</SelectItem>
                      {visibleSites.map((s) => (
                        <SelectItem key={s._id} value={s._id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </Grid>
              <Field label="Beosztás" testId="form-position">
                <Input
                  value={form.position}
                  onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                  placeholder="pl. Boltvezető"
                />
              </Field>
            </Section>

            <Section title="Születési adatok">
              <Field label="Anyja neve" testId="form-mother-name">
                <Input
                  value={form.mother_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, mother_name: e.target.value }))
                  }
                  placeholder="Nagy Éva"
                />
              </Field>
              <Grid cols={2}>
                <Field label="Születési hely" testId="form-birth-place">
                  <Input
                    value={form.birth_place}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, birth_place: e.target.value }))
                    }
                    placeholder="Budapest"
                  />
                </Field>
                <Field label="Születési dátum" testId="form-birth-date">
                  <DateInput
                    value={form.birth_date}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, birth_date: v }))
                    }
                    maxDate={new Date()}
                  />
                </Field>
              </Grid>
            </Section>

            <Section title="Azonosító okmányok">
              <Grid cols={3}>
                <Field label="TAJ szám" testId="form-taj">
                  <Input
                    value={form.taj}
                    onChange={(e) => setForm((f) => ({ ...f, taj: e.target.value }))}
                    placeholder="123 456 789"
                    inputMode="numeric"
                    className="font-mono"
                  />
                </Field>
                <Field label="Adóazonosító" testId="form-tax-id">
                  <Input
                    value={form.tax_id}
                    onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))}
                    placeholder="8412345678"
                    inputMode="numeric"
                    className="font-mono"
                  />
                </Field>
                <Field label="Személyi ig. szám" testId="form-id-card-number">
                  <Input
                    value={form.id_card_number}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, id_card_number: e.target.value }))
                    }
                    placeholder="123456 AB"
                    className="font-mono"
                  />
                </Field>
              </Grid>
            </Section>

            <Section title="POS hozzáférés">
              <Grid cols={2}>
                <Field label="POS ID" testId="form-pos-id">
                  <Input
                    value={form.pos_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, pos_id: e.target.value }))
                    }
                    placeholder="pl. K0123"
                    className="font-mono"
                    data-testid="input-pos-id"
                  />
                </Field>
                <Field label="POS jelszó" testId="form-pos-password">
                  <Input
                    value={form.pos_password}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, pos_password: e.target.value }))
                    }
                    placeholder="•••••"
                    className="font-mono"
                    data-testid="input-pos-password"
                  />
                </Field>
              </Grid>
            </Section>
            <Section title="Címek">
              <Field label="Lakcím" testId="form-address">
                <Textarea
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="1234 Budapest, Példa utca 12. 3/4"
                  rows={2}
                />
              </Field>
              <Field label="Levelezési cím" testId="form-mailing-address">
                <Textarea
                  value={form.mailing_address}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, mailing_address: e.target.value }))
                  }
                  placeholder="Megegyezik a lakcímmel, vagy más cím…"
                  rows={2}
                />
              </Field>
            </Section>

            <Section title="Elérhetőség">
              <Grid cols={2}>
                <Field label="E-mail cím" testId="form-email">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="nev@sipxgroup.hu"
                  />
                </Field>
                <Field label="Telefon" testId="form-phone">
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+36 30 123 4567"
                  />
                </Field>
              </Grid>
            </Section>

            <Section title="Pénzügyi adatok">
              <Grid cols={2}>
                <Field label="Bankszámlaszám" testId="form-bank-account">
                  <Input
                    value={form.bank_account_number}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        bank_account_number: e.target.value,
                      }))
                    }
                    placeholder="12345678-12345678-12345678"
                  />
                </Field>
                <Field label="Bank neve" testId="form-bank-name">
                  <Input
                    value={form.bank_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, bank_name: e.target.value }))
                    }
                    placeholder="pl. OTP Bank"
                  />
                </Field>
              </Grid>
              <Field label="Kifizetés módja" testId="form-payment-method">
                <Select
                  value={form.payment_method || NULL_SELECT}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      payment_method: v === NULL_SELECT ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NULL_SELECT}>—</SelectItem>
                    <SelectItem value="transfer">Átutalás</SelectItem>
                    <SelectItem value="cash">Készpénz</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.payment_method === "cash" && (
                <p
                  data-testid="cash-payment-warning"
                  className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
                >
                  Készpénzes kifizetés esetén jóváhagyás szükséges. Mentés után
                  az adatlapon hagyhatod jóvá.
                </p>
              )}
            </Section>

            {formError && (
              <div
                data-testid="form-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {formError}
              </div>
            )}
          </div>

          <SheetFooter className="flex-row justify-end gap-2 border-t border-border bg-[hsl(var(--sipx-elevated))] px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-employee-form"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-employee-form">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : employee ? (
                "Mentés"
              ) : (
                "Létrehozás"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/* --- Helpers exported for re-use --- */
function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <header className="border-b border-border pb-2">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Grid({ cols = 2, children }) {
  return (
    <div
      className={cn(
        "grid gap-3",
        cols === 2 && "grid-cols-1 sm:grid-cols-2",
        cols === 3 && "grid-cols-1 sm:grid-cols-3"
      )}
    >
      {children}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function statusMeta(value) {
  return STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];
}

export function genderLabel(value) {
  return GENDER_OPTIONS.find((g) => g.value === value)?.label || "—";
}

export function fullName(emp) {
  return `${emp.last_name || ""} ${emp.first_name || ""}`.trim() || "—";
}

export function initials(emp) {
  const a = (emp.last_name || "")[0] || "";
  const b = (emp.first_name || "")[0] || "";
  return (a + b).toUpperCase() || "—";
}

export function formatDateHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

export function EmployeeStatusBadge({ value, onboarding }) {
  // Onboarding override — minden más státuszt felülír amíg az onboarding
  // folyamatban van. Indigo gradient, hogy szeparálódjon a sima állapotoktól.
  if (onboarding) {
    return (
      <span
        data-testid="employee-status-onboarding"
        className="inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded-full border border-indigo-300/70 bg-gradient-to-r from-indigo-50 to-violet-50 px-2 text-[11px] font-semibold text-indigo-700 dark:border-indigo-500/30 dark:from-indigo-500/10 dark:to-violet-500/10 dark:text-indigo-300"
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500" />
        Onboarding
      </span>
    );
  }
  const meta = statusMeta(value);
  return (
    <span
      data-testid={`employee-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[11px] font-medium",
        STATUS_TONES[meta.tone]
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_TONES[meta.tone])} />
      {meta.label}
    </span>
  );
}
